import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosPrivate from 'config/axiosPrivate';
import axiosSmartContent from 'config/axiosSmartContent';
import {
  transformToSemanticGap,
  extractKeywordsWithFrequency,
  extractSemanticGapFromContentScore,
  formatKeywordData,
} from 'helpers/FormatKeywordsHelper';
import {
  CONTENT_TYPE_CONFIG,
  GENERATION_ERROR_MESSAGE,
  buildYoutubeApiUrl,
  buildIaHeadersForTitleMeta,
  extractField,
  formatGeneratedTextToHtml,
  formatModelName,
  getLlmIdFromAgentModel,
  getPresetForWords,
  getTextFromHtml,
  getTitleAndMetaUrls,
  isGenerationLoadingContent,
  llmIdToApiKey,
  normalizeLlmId,
  resolveContentTypeKey,
  resolveProjectLlm,
  wordsFromPresetForm,
} from 'utils/smartContent/smartContentGenerationUtils';
import {
  buildGptStreamUrl,
  buildIaHeadersForRedactionText,
  buildRedactionUrl,
} from 'utils/smartContent/textGenerationApi';
import { isPaymentRequiredError } from 'utils/smartContent/creditConsumption';
import { resolveLanguageDisplayLabel } from 'utils/smartContent/translationLanguageUtils';
import { formatWebsiteThematicsService } from 'utils/websiteThematics';

const ENRICHMENT_LLM_OPTIONS = [
  { id: 'claude', name: 'Claude' },
  { id: 'gemini', name: 'Gemini' },
];

const extractTitleFromResponse = (response) =>
  (extractField(response, ['title', 'titleTag']) || '').toString().trim();

const extractMetaFromResponse = (response) =>
  (extractField(response, ['metaDescription', 'metaDescriptionTag']) || '').toString().trim();

export const useAdminClientSmartTextGeneration = ({
  order,
  redactionData,
  projectSummary,
  keyword,
  url,
  titleTag,
  metaDescriptionTag,
  setKeyword,
  setUrl,
  setTitleTag,
  setMetaDescriptionTag,
  list,
  combinedKeywords,
  setList,
  setKeywords,
  setCombinedKeywords,
  websites,
  setWebsites,
  initialTextContent,
  initialHtmlContent,
  actualHtmlContent,
  actualTextContent,
  onContentGenerated,
  editorRef,
}) => {
  const { t } = useTranslation();

  const [wordsPerText, setWordsPerText] = useState(0);
  const [writingAxis, setWritingAxis] = useState('');
  const [existingText, setExistingText] = useState('');
  const [retranscription, setRetranscription] = useState('');
  const [availableAiModels, setAvailableAiModels] = useState([]);
  const [gapAnalysisEnabled, setGapAnalysisEnabled] = useState(false);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [isSettingsPopinOpen, setIsSettingsPopinOpen] = useState(false);
  const [isCreditsLimitModalOpen, setIsCreditsLimitModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegeneratingTitle, setIsRegeneratingTitle] = useState(false);
  const [isRegeneratingMeta, setIsRegeneratingMeta] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [resolvedWebsite, setResolvedWebsite] = useState(null);
  const [agentContentType, setAgentContentType] = useState('');

  const streamingContentRef = useRef('');

  const contentTypeKey = useMemo(
    () => resolveContentTypeKey(order, projectSummary),
    [order, projectSummary]
  );
  const contentConfig = CONTENT_TYPE_CONFIG[contentTypeKey] || CONTENT_TYPE_CONFIG.PRODUCT;

  const projectAgent = useMemo(() => {
    const id = String(
      redactionData?.aiAgentId ||
      order?.aiAgentId ||
      projectSummary?.projectAiAgent?.id ||
      ''
    ).trim();
    if (!id) return null;

    const summaryAgent = projectSummary?.projectAiAgent || {};
    const orderAgent = order?.aiAgent || {};

    return {
      ...summaryAgent,
      ...orderAgent,
      id,
      agentId: id,
      name:
        summaryAgent.name ||
        order?.aiAgentName ||
        orderAgent.name ||
        orderAgent.agentName ||
        redactionData?.aiAgentName ||
        `Agent ${id}`,
      langue: summaryAgent.langue || orderAgent.langue || orderAgent.language,
      typeContenu:
        agentContentType ||
        summaryAgent.typeContenu ||
        orderAgent.typeContenu ||
        orderAgent.contentType,
    };
  }, [redactionData, order, projectSummary, agentContentType]);

  const agentId = projectAgent?.id || projectAgent?.agentId || '';
  const selectedAgent = projectAgent;
  const redactionAgentsList = projectAgent ? [projectAgent] : [];

  const isYoutubeProject = useMemo(() => {
    if (contentConfig?.service === 'YOUTUBE') return true;

    const resolvedType = String(
      agentContentType ||
      projectAgent?.typeContenu ||
      projectSummary?.projectAiAgent?.typeContenu ||
      projectSummary?.projectAiAgent?.contentType ||
      order?.aiAgent?.typeContenu ||
      order?.aiAgent?.contentType ||
      ''
    ).toUpperCase();
    if (resolvedType === 'YOUTUBE') return true;

    const orderFormatType = String(order?.contentFormat || '').toUpperCase();
    return orderFormatType === 'YOUTUBE';
  }, [
    contentConfig,
    agentContentType,
    projectAgent,
    projectSummary,
    order,
  ]);

  useEffect(() => {
    let cancelled = false;

    const fetchAgentContentType = async () => {
      if (!agentId) {
        setAgentContentType('');
        return;
      }

      try {
        const response = await axiosPrivate.get(`/smart-content/agent_ia/${agentId}`);
        if (cancelled) return;
        const agentType = String(
          response?.data?.typeContenu || response?.data?.contentType || ''
        ).toUpperCase();
        setAgentContentType(agentType);
      } catch {
        if (!cancelled) setAgentContentType('');
      }
    };

    fetchAgentContentType();

    return () => {
      cancelled = true;
    };
  }, [agentId]);

  const resolveIsYoutubeProject = useCallback(async () => {
    if (isYoutubeProject) return true;
    if (!agentId) return false;

    try {
      const response = await axiosPrivate.get(`/smart-content/agent_ia/${agentId}`);
      const agentType = String(
        response?.data?.typeContenu || response?.data?.contentType || ''
      ).toUpperCase();
      if (agentType) setAgentContentType(agentType);
      return agentType === 'YOUTUBE';
    } catch {
      return false;
    }
  }, [isYoutubeProject, agentId]);

  const projectLlm = useMemo(
    () => {
      if (isYoutubeProject) return 'gpt';

      const agentTypeContenu = String(
        agentContentType ||
        projectSummary?.projectAiAgent?.typeContenu ||
        projectSummary?.projectAiAgent?.contentType ||
        ''
      ).toUpperCase();

      // Forcer GPT pour l'agent IA YouTube (désactiver les autres LLM)
      if (agentTypeContenu === 'YOUTUBE') return 'gpt';

      return resolveProjectLlm(order, redactionData, projectSummary);
    },
    [isYoutubeProject, agentContentType, order, redactionData, projectSummary]
  );

  const websiteUrl = useMemo(
    () => order?.website?.urlSite || order?.urlWebsite || projectSummary?.websiteUrl || '',
    [order, projectSummary]
  );

  const projectWebsite = useMemo(() => {
    if (resolvedWebsite?.thematics?.length) return resolvedWebsite;
    if (order?.website?.thematics?.length) return order.website;
    if (Array.isArray(order?.thematics) && order.thematics.length > 0) {
      return { thematics: order.thematics };
    }
    if (order?.website) return order.website;
    return resolvedWebsite;
  }, [order, resolvedWebsite]);

  useEffect(() => {
    if (order?.website?.thematics?.length || order?.thematics?.length) {
      return;
    }

    const websiteId =
      redactionData?.websiteId ||
      order?.websiteId ||
      order?.website?.id;

    if (!websiteId) return;

    let cancelled = false;

    const fetchWebsiteThematics = async () => {
      try {
        const { data } = await axiosPrivate.get(`/project/manager/website/${websiteId}`);
        if (!cancelled && data) {
          setResolvedWebsite(data);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des thématiques du site:', error);
      }
    };

    fetchWebsiteThematics();

    return () => {
      cancelled = true;
    };
  }, [
    redactionData?.websiteId,
    order?.website,
    order?.websiteId,
    order?.thematics,
  ]);

  const resolveWebsiteUrl = useCallback(
    (overrideWebsite) => {
      if (overrideWebsite) return overrideWebsite;
      return websiteUrl;
    },
    [websiteUrl]
  );

  const getSelectedLLM = useCallback(
    (overrideLlm, { isEnrichment = false } = {}) => {
      let selected = llmIdToApiKey(overrideLlm || projectLlm);
      if (
        isEnrichment &&
        selected !== 'gpt' &&
        selected !== 'claude' &&
        selected !== 'gemini'
      ) {
        selected = 'claude';
      }
      return selected;
    },
    [projectLlm]
  );

  const buildGapWords = useCallback(
    (overrides = {}) => {
      const effectiveList = overrides.list !== undefined ? overrides.list : list;
      const effectiveCombinedKeywords =
        overrides.combinedKeywords !== undefined
          ? overrides.combinedKeywords
          : combinedKeywords;

      if (effectiveList && Array.isArray(effectiveList) && effectiveList.length > 0) {
        return effectiveList
          .map((g) => {
            if (typeof g === 'string') return g;
            const word = (g?.word || '').toString().trim();
            const freq = Number(g?.frequency ?? 1);
            return word ? `${word}(${isNaN(freq) ? 1 : freq})` : '';
          })
          .map((s) => (s || '').trim())
          .filter(Boolean);
      }

      if (effectiveCombinedKeywords && effectiveCombinedKeywords.trim()) {
        return transformToSemanticGap(effectiveCombinedKeywords)
          .map((g) => {
            const word = (g?.word || '').toString().trim();
            const freq = Number(g?.frequency ?? 1);
            return word ? `${word}(${isNaN(freq) ? 1 : freq})` : '';
          })
          .map((s) => (s || '').trim())
          .filter(Boolean);
      }

      return [];
    },
    [list, combinedKeywords]
  );

  const resolvePayloadService = useCallback(
    (overrides = {}) =>
      overrides.websiteThematicsService ||
      formatWebsiteThematicsService(projectWebsite),
    [projectWebsite]
  );

  const buildPayload = useCallback(
    (overrides = {}) => {
      const gapWords = buildGapWords(overrides);
      const effectiveWebsites =
        overrides.websites !== undefined ? overrides.websites : websites;
      const effectiveWritingAxis =
        overrides.writingAxis !== undefined ? overrides.writingAxis : writingAxis;
      const effectiveAgentId =
        overrides.agentId !== undefined ? overrides.agentId : agentId;
      const effectiveWordsPerText =
        overrides.wordsPerText !== undefined ? overrides.wordsPerText : wordsPerText;
      const effectiveKeyword =
        overrides.keyword !== undefined ? overrides.keyword : keyword;
      const effectiveUrl = overrides.url !== undefined ? overrides.url : url;
      const effectiveContent =
        overrides.content !== undefined ? overrides.content : existingText;
      const maillageInterne = (effectiveWebsites || '')
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const payload = {
        agentId: effectiveAgentId || '',
        nombreMots: String(effectiveWordsPerText || 0),
        keyword: effectiveKeyword,
        urlPage: effectiveUrl,
        website: resolveWebsiteUrl(overrides.website),
        service: resolvePayloadService(overrides),
        gap: gapWords.length > 0 ? gapWords : [],
        maillageInterne: maillageInterne.length > 0 ? maillageInterne : [],
        axeRedaction: effectiveWritingAxis || '',
      };

      const trimmedContent = String(effectiveContent || '').trim();
      if (contentConfig?.service === 'CONTENT_IMPROVEMENT' && trimmedContent) {
        payload.content = trimmedContent;
      }

      const hasExplicitIds =
        Array.isArray(overrides.keptKnowledgeBaseFileIds) ||
        Array.isArray(overrides.knowledgeBaseFileIds);
      const knowledgeBaseFileIds = (
        Array.isArray(overrides.keptKnowledgeBaseFileIds)
          ? overrides.keptKnowledgeBaseFileIds
          : Array.isArray(overrides.knowledgeBaseFileIds)
            ? overrides.knowledgeBaseFileIds
            : (Array.isArray(redactionData?.knowledgeBaseFiles)
              ? redactionData.knowledgeBaseFiles
              : []
            ).map((file) => file?.id || file?.fileId)
      )
        .map((id) => String(id || '').trim())
        .filter(Boolean);

      if (hasExplicitIds || knowledgeBaseFileIds.length > 0) {
        payload.knowledgeBaseFileIds = knowledgeBaseFileIds;
      }

      return payload;
    },
    [
      buildGapWords,
      websites,
      writingAxis,
      agentId,
      wordsPerText,
      keyword,
      url,
      existingText,
      resolveWebsiteUrl,
      resolvePayloadService,
      contentConfig,
      redactionData?.knowledgeBaseFiles,
    ]
  );

  const buildYoutubePayload = useCallback(
    (overrides = {}) => {
      const gapWords = buildGapWords(overrides);
      const effectiveWebsites =
        overrides.websites !== undefined ? overrides.websites : websites;
      const effectiveAgentId =
        overrides.agentId !== undefined ? overrides.agentId : agentId;
      const effectiveWordsPerText =
        overrides.wordsPerText !== undefined ? overrides.wordsPerText : wordsPerText;
      const effectiveKeyword =
        overrides.keyword !== undefined ? overrides.keyword : keyword;
      const effectiveUrl = overrides.url !== undefined ? overrides.url : url;
      const effectiveContent =
        overrides.content !== undefined ? overrides.content : existingText;
      const effectiveRetranscription =
        overrides.retranscription !== undefined ? overrides.retranscription : retranscription;
      const maillageInterne = (effectiveWebsites || '')
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const payload = {
        agentId: String(effectiveAgentId || ''),
        website: resolveWebsiteUrl(overrides.website),
        service: resolvePayloadService(overrides),
        originalText: String(effectiveContent || '').trim(),
        urlPage: String(effectiveUrl || ''),
        keyword: String(effectiveKeyword || ''),
        nombreMots: String(effectiveWordsPerText || 0),
        retranscription: String(effectiveRetranscription || '').trim(),
        gap: gapWords.length > 0 ? gapWords : [],
        maillageInterne,
      };

      const hasExplicitIds =
        Array.isArray(overrides.keptKnowledgeBaseFileIds) ||
        Array.isArray(overrides.knowledgeBaseFileIds);
      const knowledgeBaseFileIds = (
        Array.isArray(overrides.keptKnowledgeBaseFileIds)
          ? overrides.keptKnowledgeBaseFileIds
          : Array.isArray(overrides.knowledgeBaseFileIds)
            ? overrides.knowledgeBaseFileIds
            : (Array.isArray(redactionData?.knowledgeBaseFiles)
              ? redactionData.knowledgeBaseFiles
              : []
            ).map((file) => file?.id || file?.fileId)
      )
        .map((id) => String(id || '').trim())
        .filter(Boolean);

      if (hasExplicitIds || knowledgeBaseFileIds.length > 0) {
        payload.knowledgeBaseFileIds = knowledgeBaseFileIds;
      }

      return payload;
    },
    [
      buildGapWords,
      websites,
      agentId,
      wordsPerText,
      keyword,
      url,
      existingText,
      retranscription,
      resolveWebsiteUrl,
      resolvePayloadService,
      redactionData?.knowledgeBaseFiles,
    ]
  );

  const refreshCompanyQuotas = useCallback(async () => {
    const { data } = await axiosPrivate.get('/project/company-quotas');
    setGapAnalysisEnabled(data?.gapAnalysisEnabled === true);
    setTranslationEnabled(data?.translationEnabled !== false);
    const totalCredits = Number(data?.totalCredits);
    const usedCredits = Number(data?.usedCredits);
    if (Number.isFinite(totalCredits) && Number.isFinite(usedCredits)) {
      setRemainingCredits(Math.max(0, totalCredits - usedCredits));
    } else {
      const apiRemainingCredits = Number(data?.remainingCredits);
      setRemainingCredits(
        Number.isFinite(apiRemainingCredits) ? apiRemainingCredits : null
      );
    }
    const rawModels = Array.isArray(data?.availableAiModels) ? data.availableAiModels : [];
    const normalizedModels = rawModels
      .map((model) => {
        const id = normalizeLlmId(
          typeof model === 'string' ? model : model?.id || model?.value || model?.name || ''
        );
        if (!id) return null;
        return {
          id,
          name: typeof model === 'object' && model?.name ? model.name : formatModelName(id),
        };
      })
      .filter(Boolean);
    setAvailableAiModels(
      Array.from(new Map(normalizedModels.map((item) => [item.id, item])).values())
    );
  }, []);

  useEffect(() => {
    refreshCompanyQuotas().catch(() => {
      setGapAnalysisEnabled(false);
      setAvailableAiModels([]);
      setRemainingCredits(null);
    });
  }, [refreshCompanyQuotas]);

  useEffect(() => {
    const resolvedWords =
      Number(redactionData?.numberWords) ||
      Number(order?.maxWords) ||
      Number(order?.numberOfWords) ||
      Number(projectSummary?.projectMaxWords) ||
      0;
    setWordsPerText(resolvedWords);

    setWritingAxis(redactionData?.axeRedaction || redactionData?.writingAxis || '');
    setRetranscription(redactionData?.retranscription || '');

    const initialContent =
      String(initialTextContent || '').trim() ||
      getTextFromHtml(initialHtmlContent).trim();
    setExistingText(initialContent);
  }, [
    redactionData,
    order,
    projectSummary,
    initialTextContent,
    initialHtmlContent,
  ]);

  const applySemanticGapState = useCallback(
    (semanticGapInput) => {
      if (Array.isArray(semanticGapInput) && semanticGapInput.length > 0) {
        setList(semanticGapInput);
        setKeywords(extractKeywordsWithFrequency(semanticGapInput));
        setCombinedKeywords(formatKeywordData(semanticGapInput));
        return;
      }

      if (typeof semanticGapInput === 'string' && semanticGapInput.trim()) {
        setCombinedKeywords(semanticGapInput);
        setKeywords(extractKeywordsWithFrequency(semanticGapInput));
        setList(transformToSemanticGap(semanticGapInput));
      }
    },
    [setCombinedKeywords, setKeywords, setList]
  );

  const updateEditorContent = useCallback(
    (html) => {
      if (!html) return;
      onContentGenerated?.({
        html,
        text: getTextFromHtml(html),
      });
      if (editorRef?.current?.setContent) {
        editorRef.current.setContent(html);
      }
    },
    [editorRef, onContentGenerated]
  );

  const generateYoutubeText = useCallback(
    async (overrides = {}) => {
      const payload = buildYoutubePayload(overrides);
      const response = await axiosPrivate.post(buildYoutubeApiUrl(), payload);
      const generatedText =
        response.data?.output ||
        response.data?.description ||
        (typeof response.data === 'string' ? response.data : '');

      if (!generatedText || String(generatedText).trim() === '') {
        throw new Error('Aucun contenu généré');
      }

      await refreshCompanyQuotas();

      return {
        html: formatGeneratedTextToHtml(generatedText),
        title: '',
        meta: '',
      };
    },
    [buildYoutubePayload, refreshCompanyQuotas]
  );

  const generateText = useCallback(
    async (overrideLlm, overrides = {}) => {
      const isEnrichment = contentConfig?.service === 'CONTENT_IMPROVEMENT';
      const llm = getSelectedLLM(overrideLlm, { isEnrichment });
      const urlToCall = buildRedactionUrl(llm, { isEnrichment });
      const payload = buildPayload(overrides);
      const iaHeaders = buildIaHeadersForRedactionText(llm);
      const httpClient = isEnrichment ? axiosSmartContent : axiosPrivate;
      const response = await httpClient.post(urlToCall, payload, { headers: iaHeaders });
      const generatedText = response.data?.output || '';
      if (!generatedText || generatedText.trim() === '') {
        throw new Error('Aucun contenu généré');
      }
      await refreshCompanyQuotas();
      return {
        html: formatGeneratedTextToHtml(generatedText),
        title: extractTitleFromResponse(response),
        meta: extractMetaFromResponse(response),
      };
    },
    [buildPayload, contentConfig, getSelectedLLM, refreshCompanyQuotas]
  );

  const generateTextStreaming = useCallback(
    async (overrides = {}, streamOptions = {}) => {
      if (isYoutubeProject) {
        return generateYoutubeText(overrides);
      }

      const {
        updateTitleAndMeta = true,
        updateContent = true,
        updateTitle = updateTitleAndMeta,
        updateMeta = updateTitleAndMeta,
        requireContent = true,
        onProgress,
      } = streamOptions;

      const payload = buildPayload(overrides);
      const iaHeaders = buildIaHeadersForRedactionText('gpt');
      const streamUrl = buildGptStreamUrl();

      let sseBuffer = '';
      let lastResponseLength = 0;
      let accumulatedContent = '';
      let accumulatedTitle = '';
      let accumulatedMeta = '';

      const processSseLines = (lines) => {
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const raw = trimmed.slice(5).trim();
          if (raw === '[DONE]') continue;
          try {
            const chunk = JSON.parse(raw);
            if (!chunk.content) continue;
            if (chunk.type === 'CONTENT' && updateContent) {
              accumulatedContent += chunk.content;
              onProgress?.({ type: 'CONTENT', content: accumulatedContent });
            } else if (chunk.type === 'TITLE' && updateTitle) {
              accumulatedTitle += chunk.content;
              onProgress?.({ type: 'TITLE', content: accumulatedTitle });
            } else if (chunk.type === 'META_DESCRIPTION' && updateMeta) {
              accumulatedMeta += chunk.content;
              onProgress?.({ type: 'META', content: accumulatedMeta });
            }
          } catch {
            // skip malformed chunk
          }
        }
      };

      const processIncomingSse = (incomingText) => {
        if (!incomingText) return;
        sseBuffer += incomingText;
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop() || '';
        processSseLines(lines);
      };

      const response = await axiosPrivate.post(streamUrl, payload, {
        headers: {
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
          ...iaHeaders,
        },
        responseType: 'text',
        onDownloadProgress: (progressEvent) => {
          const responseText = progressEvent?.event?.target?.responseText;
          if (typeof responseText !== 'string') return;
          const newPart = responseText.slice(lastResponseLength);
          lastResponseLength = responseText.length;
          processIncomingSse(newPart);
        },
      });

      const finalText = typeof response.data === 'string' ? response.data : '';
      if (finalText.length > lastResponseLength) {
        processIncomingSse(finalText.slice(lastResponseLength));
      }
      if (sseBuffer.trim()) {
        processSseLines([sseBuffer]);
      }

      if (requireContent && !accumulatedContent.trim()) {
        throw new Error('Aucun contenu généré');
      }

      await refreshCompanyQuotas();

      const finalHtml = accumulatedContent.includes('<')
        ? accumulatedContent
        : `<p>${accumulatedContent.replace(/\n/g, '</p><p>')}</p>`;

      return {
        html: finalHtml,
        title: accumulatedTitle.trim(),
        meta: accumulatedMeta.trim(),
      };
    },
    [buildPayload, refreshCompanyQuotas, isYoutubeProject, generateYoutubeText]
  );

  const getLoadingHtml = useCallback(() => {
    const redactionEnCoursText = t('Rédaction en cours...');
    const generationEnCoursText = t('Génération en cours');
    return `
      <div style="text-align: center; padding: 40px;">
        <img src="/Images/gifencours.gif" alt="${generationEnCoursText}" style="width: 200px; height: 200px; margin-bottom: 20px;" />
        <p style="color: #666; font-size: 16px; font-weight: 500;">${redactionEnCoursText}</p>
      </div>
    `;
  }, [t]);

  const handleStartGeneration = useCallback(
    async (overrideLlm, overrides = {}, options = {}) => {
      if (remainingCredits !== null && remainingCredits <= 0) {
        setIsCreditsLimitModalOpen(true);
        return;
      }

      const effectiveAgentId = String(
        overrides.agentId !== undefined ? overrides.agentId : agentId || ''
      ).trim();
      const effectiveWordsPerText = Number(
        overrides.wordsPerText !== undefined ? overrides.wordsPerText : wordsPerText
      );
      const effectiveKeyword = String(
        overrides.keyword !== undefined ? overrides.keyword : keyword || ''
      ).trim();

      if (!effectiveAgentId) {
        setGenerationError(t('Veuillez sélectionner un agent IA'));
        return;
      }
      if (effectiveWordsPerText <= 0) {
        setGenerationError(t('Veuillez entrer un nombre de mots valide'));
        return;
      }
      if (!effectiveKeyword) {
        setGenerationError(t('Veuillez entrer un mot-clé'));
        return;
      }

      setIsGenerating(true);
      setGenerationError('');

      const loadingHtml = getLoadingHtml();
      updateEditorContent(loadingHtml);

      try {
        const youtubeProject = await resolveIsYoutubeProject();
        if (youtubeProject) {
          const generated = await generateYoutubeText(overrides);
          updateEditorContent(generated.html);
          return;
        }

        const isEnrichment = contentConfig?.service === 'CONTENT_IMPROVEMENT';
        const llm = getSelectedLLM(overrideLlm || projectLlm, { isEnrichment });
        const useStreaming = llm === 'gpt' && !isEnrichment && !youtubeProject;
        const shouldGenerateTitleAndMeta =
          contentConfig?.service !== 'GMB' &&
          contentConfig?.service !== 'YOUTUBE' &&
          options.generateTitleAndMeta !== false;

        if (useStreaming) {
          streamingContentRef.current = '';

          const STREAM_CURSOR =
            '<span style="display:inline-block;width:2px;height:1em;' +
            'background:#6458F1;margin-left:2px;vertical-align:text-bottom;' +
            'animation:blink 0.8s step-end infinite"></span>';
          const STREAM_UPDATE_MS = 120;
          let lastEditorUpdate = 0;

          // Le curseur vit dans l'iframe TinyMCE : injecter l'animation dedans.
          try {
            editorRef?.current?.dom?.addStyle?.(
              '@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }'
            );
          } catch {
            // ignore
          }

          const writeStreamChunk = (content, { force = false } = {}) => {
            const now = Date.now();
            if (!force && now - lastEditorUpdate < STREAM_UPDATE_MS) return;
            lastEditorUpdate = now;
            const partialHtml = content.includes('<')
              ? content
              : `<p>${content.replace(/\n/g, '</p><p>')}</p>`;
            if (editorRef?.current?.setContent) {
              editorRef.current.setContent(partialHtml + STREAM_CURSOR);
            }
          };

          const { html, title, meta } = await generateTextStreaming(overrides, {
            updateContent: true,
            updateTitle: shouldGenerateTitleAndMeta,
            updateMeta: shouldGenerateTitleAndMeta,
            requireContent: true,
            onProgress: ({ type, content }) => {
              if (type === 'CONTENT') {
                streamingContentRef.current = content;
                writeStreamChunk(content);
              } else if (type === 'TITLE') {
                setTitleTag(content);
              } else if (type === 'META') {
                setMetaDescriptionTag(content);
              }
            },
          });

          updateEditorContent(html);
          if (shouldGenerateTitleAndMeta) {
            if (title) setTitleTag(title);
            if (meta) setMetaDescriptionTag(meta);
          }
        } else {
          const generated = await generateText(overrideLlm || projectLlm, overrides);
          updateEditorContent(generated.html);
          if (shouldGenerateTitleAndMeta) {
            if (generated.title) setTitleTag(generated.title);
            if (generated.meta) setMetaDescriptionTag(generated.meta);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la génération:', error);
        setGenerationError(
          error?.response?.data?.message || error.message || GENERATION_ERROR_MESSAGE
        );
        updateEditorContent(`<p><em>${GENERATION_ERROR_MESSAGE}</em></p>`);
      } finally {
        setIsGenerating(false);
        try {
          await refreshCompanyQuotas();
        } catch {
          // ignore
        }
      }
    },
    [
      remainingCredits,
      agentId,
      wordsPerText,
      keyword,
      getLoadingHtml,
      updateEditorContent,
      editorRef,
      contentConfig,
      isYoutubeProject,
      resolveIsYoutubeProject,
      getSelectedLLM,
      projectLlm,
      generateYoutubeText,
      generateTextStreaming,
      setTitleTag,
      setMetaDescriptionTag,
      generateText,
      refreshCompanyQuotas,
      t,
    ]
  );

  const handleRegenerateTitle = useCallback(async () => {
    if (contentConfig?.service === 'GMB' || contentConfig?.service === 'YOUTUBE' || isYoutubeProject) {
      return;
    }
    if (remainingCredits !== null && remainingCredits <= 0) {
      setIsCreditsLimitModalOpen(true);
      return;
    }
    if (!keyword.trim() || !agentId.trim()) return;

    setIsRegeneratingTitle(true);
    setTitleTag('');
    try {
      const llm = getSelectedLLM(projectLlm);
      const { titleUrl } = getTitleAndMetaUrls(
        keyword,
        resolveWebsiteUrl(),
        resolveLanguageDisplayLabel(selectedAgent?.langue || selectedAgent?.language),
        llm
      );
      const iaHeaders = buildIaHeadersForTitleMeta(llm);
      const titleRes = await axiosSmartContent.post(titleUrl, {}, { headers: iaHeaders });
      const nextTitle = (
        extractField(titleRes, ['output', 'title', 'result', 'content']) || ''
      )
        .toString()
        .trim();
      if (nextTitle) setTitleTag(nextTitle);
    } catch (err) {
      console.error('Erreur lors de la régénération du titre:', err);
      if (isPaymentRequiredError(err)) {
        setIsCreditsLimitModalOpen(true);
      } else {
        setGenerationError(t('Erreur lors de la régénération du titre'));
      }
    } finally {
      setIsRegeneratingTitle(false);
      refreshCompanyQuotas().catch(() => { });
    }
  }, [
    contentConfig,
    remainingCredits,
    keyword,
    agentId,
    getSelectedLLM,
    projectLlm,
    resolveWebsiteUrl,
    selectedAgent,
    setTitleTag,
    refreshCompanyQuotas,
    t,
  ]);

  const handleRegenerateMeta = useCallback(async () => {
    if (contentConfig?.service === 'GMB' || contentConfig?.service === 'YOUTUBE' || isYoutubeProject) {
      return;
    }
    if (remainingCredits !== null && remainingCredits <= 0) {
      setIsCreditsLimitModalOpen(true);
      return;
    }
    if (!keyword.trim() || !agentId.trim()) return;

    setIsRegeneratingMeta(true);
    setMetaDescriptionTag('');
    try {
      const llm = getSelectedLLM(projectLlm);
      const { metaUrl } = getTitleAndMetaUrls(
        keyword,
        resolveWebsiteUrl(),
        resolveLanguageDisplayLabel(selectedAgent?.langue || selectedAgent?.language),
        llm
      );
      const iaHeaders = buildIaHeadersForTitleMeta(llm);
      const metaRes = await axiosSmartContent.post(metaUrl, {}, { headers: iaHeaders });
      const nextMeta = (
        extractField(metaRes, ['output', 'metaDescription', 'result', 'content']) || ''
      )
        .toString()
        .trim();
      if (nextMeta) setMetaDescriptionTag(nextMeta);
    } catch (err) {
      console.error('Erreur lors de la régénération de la méta:', err);
      if (isPaymentRequiredError(err)) {
        setIsCreditsLimitModalOpen(true);
      } else {
        setGenerationError(t('Erreur lors de la régénération de la méta-description'));
      }
    } finally {
      setIsRegeneratingMeta(false);
      refreshCompanyQuotas().catch(() => { });
    }
  }, [
    contentConfig,
    remainingCredits,
    keyword,
    agentId,
    getSelectedLLM,
    projectLlm,
    resolveWebsiteUrl,
    selectedAgent,
    setMetaDescriptionTag,
    refreshCompanyQuotas,
    t,
  ]);

  const textKnowledgeBaseFiles = Array.isArray(redactionData?.knowledgeBaseFiles)
    ? redactionData.knowledgeBaseFiles
    : [];

  const getPopinInitialValues = useCallback(
    () => {
      const gapFromScore = extractSemanticGapFromContentScore(
        redactionData?.contentScore
      );
      const semanticGapValue =
        (combinedKeywords && String(combinedKeywords).trim()) ||
        (gapFromScore.length > 0 ? formatKeywordData(gapFromScore) : '') ||
        '';

      return {
        keyword,
        agentId: agentId || '',
        llm: projectLlm,
        targetUrl: url || '',
        existingText: existingText || '',
        retranscription: retranscription || '',
        semanticGap: semanticGapValue,
        internalLinking: websites || '',
        writingAxis: writingAxis || '',
        knowledgeBaseFiles: textKnowledgeBaseFiles,
        ...getPresetForWords(wordsPerText),
      };
    },
    [
      keyword,
      agentId,
      projectLlm,
      url,
      existingText,
      retranscription,
      combinedKeywords,
      websites,
      writingAxis,
      wordsPerText,
      textKnowledgeBaseFiles,
      redactionData?.contentScore,
    ]
  );

  const handleSettingsSubmit = useCallback(
    (form) => {
      const words = wordsFromPresetForm(form);

      if (form.keyword) setKeyword(form.keyword);
      if (form.targetUrl !== undefined) setUrl(form.targetUrl || '');

      const nextExistingText = (form.existingText ?? form.content ?? '').trim();
      setExistingText(nextExistingText);

      if (form.retranscription !== undefined) {
        setRetranscription(form.retranscription || '');
      }
      if (form.internalLinking !== undefined) {
        setWebsites(form.internalLinking || '');
      }
      const nextWritingAxis = form.writingAxis ?? form.axeRedaction;
      if (nextWritingAxis !== undefined) {
        setWritingAxis(nextWritingAxis || '');
      }
      if (words) setWordsPerText(words);
      if (form.semanticGap) {
        try {
          applySemanticGapState(form.semanticGap);
        } catch {
          // ignore
        }
      }

      setIsSettingsPopinOpen(false);

      handleStartGeneration(projectLlm, {
        keyword: form.keyword || keyword,
        url: form.targetUrl || url,
        agentId,
        wordsPerText: words || wordsPerText,
        websites:
          form.internalLinking !== undefined ? form.internalLinking || '' : websites,
        writingAxis:
          nextWritingAxis !== undefined ? nextWritingAxis || '' : writingAxis,
        content: nextExistingText,
        combinedKeywords:
          form.semanticGap !== undefined ? form.semanticGap || '' : combinedKeywords,
        retranscription:
          form.retranscription !== undefined ? form.retranscription || '' : retranscription,
        files: form.knowledgeBaseFiles || form.files || [],
        ...(Array.isArray(form.keptKnowledgeBaseFileIds)
          ? { keptKnowledgeBaseFileIds: form.keptKnowledgeBaseFileIds }
          : Array.isArray(form.knowledgeBaseFileIds)
            ? { keptKnowledgeBaseFileIds: form.knowledgeBaseFileIds }
            : {}),
      });
    },
    [
      setKeyword,
      setUrl,
      setWebsites,
      applySemanticGapState,
      handleStartGeneration,
      projectLlm,
      keyword,
      url,
      agentId,
      wordsPerText,
      websites,
      writingAxis,
      combinedKeywords,
      retranscription,
    ]
  );

  const hasContentOrGenerated = useMemo(() => {
    const html = actualHtmlContent || actualTextContent || '';
    return getTextFromHtml(html).trim().length > 0 && !isGenerationLoadingContent(html);
  }, [actualHtmlContent, actualTextContent]);

  const isCreditsExhausted = remainingCredits !== null && remainingCredits <= 0;

  const aiAgentModel = useMemo(() => {
    const llm = projectLlm;
    if (llm === 'claude') return 'CLAUDE';
    if (llm === 'gpt') return 'GPT';
    if (llm === 'claude_gpt') return 'GPT + CLAUDE';
    if (llm === 'perplexity') return 'PERPLEXITY';
    if (llm === 'gemini') return 'GEMINI';
    if (llm === 'premium') return 'PREMIUM';
    return getLlmIdFromAgentModel(order?.llmType || redactionData?.aiAgentModel || '');
  }, [projectLlm, order, redactionData]);

  const agentName = useMemo(
    () =>
      selectedAgent?.name ||
      selectedAgent?.agentName ||
      projectSummary?.projectAiAgent?.name ||
      order?.aiAgentName ||
      '',
    [selectedAgent, projectSummary, order]
  );

  return {
    contentConfig,
    contentTypeKey,
    projectLlm,
    isGenerating,
    isRegeneratingTitle,
    isRegeneratingMeta,
    isSettingsPopinOpen,
    setIsSettingsPopinOpen,
    isCreditsLimitModalOpen,
    setIsCreditsLimitModalOpen,
    remainingCredits,
    isCreditsExhausted,
    generationError,
    redactionAgentsList,
    availableAiModels,
    websiteUrl,
    gapAnalysisEnabled,
    translationEnabled,
    hasContentOrGenerated,
    selectedAgent,
    agentName,
    aiAgentModel,
    handleStartGeneration: () =>
      handleStartGeneration(projectLlm, {
        keptKnowledgeBaseFileIds: (Array.isArray(redactionData?.knowledgeBaseFiles)
          ? redactionData.knowledgeBaseFiles
          : []
        )
          .map((file) => String(file?.id || file?.fileId || '').trim())
          .filter(Boolean),
      }),
    handleRegenerateTitle,
    handleRegenerateMeta,
    handleSettingsSubmit,
    getPopinInitialValues,
    enrichmentLlmOptions: ENRICHMENT_LLM_OPTIONS,
    refreshCompanyQuotas,
  };
};
